/**
 * Registry degli STRUMENTI del Cartellino (dominio GI Presenze).
 *
 * Il Catalogo (UI) e' un componente Foundation generico
 * (`foundation/actions/ActionCatalog`): questo file dichiara SOLO le azioni
 * del modulo Presenze e le mappa nel contratto `Action` della Foundation.
 *
 * REGOLA: gli strumenti "in arrivo" NON compaiono in questo file. Le funzioni
 * future vengono registrate solo quando sono realmente operative.
 * Aggiungere una nuova azione = 1 entry + funzione onExecute.
 *
 * LINEA GUIDA ARCHITETTURALE (v1.5.5):
 *   Il Cartellino distingue nettamente tra OPERAZIONI SUL CARTELLINO
 *   (compilazione, modifica, salvataggio) e OPERAZIONI DI WORKFLOW
 *   (invio, approvazione, respingimento, chiusura). Le prime vivono in
 *   toolbar + questo catalogo; le seconde SOLO nel MonthWorkflowControl.
 *   Non registrare qui azioni che modificano stati di workflow.
 */
import {
  ClipboardList, Import, Calculator, Search, Layers, Sparkles,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// Categorie (chiave → { label, icon, order }). Vengono passate all'ActionCatalog.
// Nuove categorie si aggiungono qui.
// ═══════════════════════════════════════════════════════════════════════════════
export const ATTENDANCE_CATEGORIES = {
  compilazione: { label: "Compilazione", icon: ClipboardList, order: 10 },
  import:       { label: "Import",       icon: Import,        order: 20 },
  ricalcoli:    { label: "Ricalcoli",    icon: Calculator,    order: 30 },
  controlli:    { label: "Controlli",    icon: Search,        order: 40 },
  massivi:      { label: "Massivi",      icon: Layers,        order: 50 },
  ai:           { label: "AI",           icon: Sparkles,      order: 60 },
};

/**
 * Costruisce le Action del modulo Cartellino in base al contesto passato dalla
 * pagina (helpers, rows, monthData, setRows, dirtyRef).
 *
 * Aggiungere una nuova operazione = una entry con `onExecute` async.
 * Le operazioni "in arrivo" NON vengono registrate finche' non operative.
 *
 * v1.5.5: rimossa la duplicazione "Compila dal teorico" — l'azione esiste solo
 * come pulsante primario in toolbar (unico punto d'ingresso). Vedi commento
 * sotto per la storia della rimozione.
 */
export function buildAttendanceActions(ctx) {
  const actions = [];

  // NOTA (v1.5.5): "Compila dal teorico" e' stato RIMOSSO da questo registry.
  //   Motivazione: era un duplicato del pulsante primario nella toolbar del
  //   Cartellino. Il principio "un solo punto di accesso per funzione" impone
  //   che l'azione viva in un unico posto (la toolbar). Riferimento richiesto
  //   dall'utente nel messaggio del 24/02/2026 su consolidamento toolbar.
  //
  // NOTA (v1.5.4): "Compila dal profilo orario" e' stato ELIMINATO.
  //   Il calcolo teorico si basa gia' sul profilo orario del dipendente per il
  //   giorno: le due funzioni erano identiche dal punto di vista del consulente.
  //   Se in futuro emergera' una vera differenza funzionale (turnistica, orari
  //   variabili con override giornalieri), verra' registrata qui come nuova
  //   action con logica separata.

  return actions;
}

/**
 * Restituisce il titolo dell'azione "normalmente utilizzata" (hint).
 * Ritorna null se non c'e' preferenza forte (`ask`/`never`).
 * v1.5.4: usato dal componente Foundation ActionCatalog quando riutilizzato
 * da altri moduli. Nel Cartellino attualmente non e' esposto (le action
 * vivono in "Altre operazioni").
 * v1.5.5: dopo la rimozione di `fill_from_theoretical`, la lookup ritorna
 * null se l'azione non esiste piu'. Restera' utile per moduli futuri.
 */
export function getPreferredActionHint(mode, actions) {
  if (mode === "always_theoretical" || mode === "always_profile") {
    return actions.find((a) => a.id === "fill_from_theoretical")?.title || null;
  }
  return null;
}
